import { zodResolver } from '@hookform/resolvers/zod'
import { Modal } from '@mantine/core'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { SubmitHandler } from 'react-hook-form'
import type { CheckoutPocInfoFormFieldSchema } from '@/schemas/booking'
import { PointOfContactFormFieldSchema } from '@/schemas/booking'
import { getBookingQueryOptions } from '@/queries/query'
import PointOfContactModal from '@/components/make-booking/checkout/modals/PointOfContactModal'
import useBookingMutation from '@/hooks/useBookingMutation'
import { isHttpError } from '@/utils/http'

export const Route = createFileRoute('/my-booking/$id/view/poc')({
  component: RouteComponent,
})

function RouteComponent() {
  const router = useRouter()
  const bookingId = Route.useParams({
    select: (s) => s.id,
  })
  const {
    data: {
      data: { booking },
    },
  } = useSuspenseQuery(getBookingQueryOptions(bookingId))
  const form = useForm({
    mode: 'all',
    resolver: zodResolver(PointOfContactFormFieldSchema),
    defaultValues: booking,
  })

  const { updateBookingInfo } = useBookingMutation()

  const onSubmit = useCallback<SubmitHandler<CheckoutPocInfoFormFieldSchema>>(
    (data, e) => {
      e?.stopPropagation()
      e?.preventDefault()
      return updateBookingInfo.mutateAsync(
        {
          id: bookingId,
          adhoc_booking: {
            contact_name: booking.contact_name ?? data.poc_name,
            contact_email: booking.contact_email!,
            contact_phone: booking.contact_phone ?? data.poc_phone,
            is_poc_same_with_contact_info: data.is_poc_same_with_contact_info,
            poc_name: data.poc_name,
            poc_phone: data.poc_phone,
          },
        },
        {
          onSuccess: () => {
            toast.success('Point of Contact updated successfully!')
            return router.history.back()
          },
          onError: async (err) => {
            if (isHttpError(err)) {
              const res = await err.response.json()
              return toast.error('message' in res ? res.message : res.error)
            }
            return toast.error('Something went wrong')
          },
        },
      )
    },
    [booking, updateBookingInfo],
  )

  return (
    <Modal
      opened
      onClose={() => router.history.back()}
      title="Point of Contact"
      centered
      classNames={{
        title: '!text-[22px] !font-bold',
        content: '!p-5',
      }}
      size="xl"
    >
      <FormProvider {...form}>
        <PointOfContactModal
          onClose={() => router.history.back()}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </Modal>
  )
}
